import { useMetricsInterval } from "@tether-web-portal/hooks";
import { MetricDataInterval } from "@tether-web-portal/types/metricsTypes";
import { TimestampRange } from "@tether-web-portal/types/TimestampRange";
import { DateTime } from "luxon";
import { useCallback, useMemo, useState } from "react";
import { TimeButtonOptions } from "@tether-web-portal/components/button/TimeRangeButtons";
import { DatePickedEvent } from "@tether-web-portal/components/datepicker/DatePickedEvent";

const getSelectedButtonFromDateRange = (
  timeStamps: TimestampRange,
  selectedInterval: MetricDataInterval,
  buttonOptions: TimeButtonOptions[],
  zone: string,
): TimeButtonOptions | null => {
  if (selectedInterval !== MetricDataInterval.CUSTOM) {
    return buttonOptions.find((button) => button.interval === selectedInterval) || null;
  }

  // end date is start date check if is one of the known ranges
  const match = buttonOptions.find((button) => {
    // Check end dates match
    if (
      button.endDate &&
      DateTime.fromISO(button.endDate, { zone: "utc" }).setZone(zone).toFormat("yyyy-MM-dd") !==
        DateTime.fromISO(timeStamps.toTimestamp, { zone: "utc" }).setZone(zone).toFormat("yyyy-MM-dd")
    ) {
      return false;
    }

    if (!!button.hours) {
      const useYearMonthDay = button.hours > 24;

      const safeEndDate = button.endDate
        ? DateTime.fromISO(button.endDate, { zone: "utc" }).setZone(zone)
        : DateTime.now().setZone(zone);
      const endDate = button.hours < 25 ? safeEndDate.endOf("minute") : safeEndDate.endOf("day");
      const startDate = endDate.minus({ minutes: button.hours * 60 });
      const luxonDateFormatToken = useYearMonthDay ? "yyyy-MM-dd" : "dd/MM/yyyy HH a";
      const formattedStartDate = startDate.toFormat(luxonDateFormatToken);
      const formattedCurrentFromDate = DateTime.fromISO(timeStamps.fromTimestamp, { zone: "utc" })
        .setZone(zone)
        .toFormat(luxonDateFormatToken);
      if (formattedStartDate !== formattedCurrentFromDate) {
        return false;
      }

      return true;
    }

    return false;
  });

  return match || null;
};

interface useDateRangeControlsProps {
  buttonOptions: TimeButtonOptions[];
  timezone: string;
}
export const useDateRangeControls = ({ buttonOptions, timezone }: useDateRangeControlsProps) => {
  const { metricsTimeStamps, metricsCurrentDataInterval, setMetricsInterval, setCustomMetricsInterval } =
    useMetricsInterval();

  const selectedButton = useMemo(
    () =>
      getSelectedButtonFromDateRange(metricsTimeStamps, metricsCurrentDataInterval, buttonOptions, timezone),
    [buttonOptions, metricsTimeStamps, metricsCurrentDataInterval, timezone],
  );

  const [customDateRange, setCustomDateRange] = useState<{
    startDate: string | null;
    endDate: string | null;
  }>({
    startDate: null,
    endDate: null,
  });

  const setNewTimeRangeFromButton = useCallback(
    (button: TimeButtonOptions) => {
      if (button.interval !== MetricDataInterval.CUSTOM) {
        setMetricsInterval(button.interval);
      } else if (button.hours) {
        const safeEndDate = button.endDate
          ? DateTime.fromISO(button.endDate, { zone: "utc" }).setZone(timezone)
          : DateTime.now().setZone(timezone);
        const endDate = button.hours < 25 ? safeEndDate.endOf("minute") : safeEndDate.endOf("day");
        const startDate = endDate.minus({ minutes: button.hours * 60 });

        const fromTimestamp = startDate.toUTC().toISO();
        const toTimestamp = endDate.toUTC().toISO();
        if (fromTimestamp && toTimestamp) {
          setCustomMetricsInterval(fromTimestamp, toTimestamp);
        }
      }
    },
    [setMetricsInterval, setCustomMetricsInterval, timezone],
  );

  const onDatesChange = useCallback(
    (data: DatePickedEvent) => {
      const startIso = data.startDate
        ? DateTime.fromISO(data.startDate.toISOString(), { zone: timezone }).startOf("day").toUTC().toISO()
        : null;
      const endIso = data.endDate
        ? DateTime.fromISO(data.endDate.toISOString(), { zone: timezone }).endOf("day").toUTC().toISO()
        : null;
      setCustomDateRange({
        startDate: startIso,
        endDate: endIso,
      });
      if (startIso && endIso) {
        setCustomMetricsInterval(startIso, endIso);
      }
    },
    [setCustomDateRange, setCustomMetricsInterval, timezone],
  );

  return {
    selectedButton,
    setNewTimeRangeFromButton,
    customDateRange,
    onDatesChange,
  };
};
